import PropTypes from 'prop-types'
import { useTheme, keyframes } from '@emotion/react'
import clsx from 'clsx'
import { Section } from 'chaoskit/src/components'

import Image from '~components/Image'
import charter from '~media/logos/charter.svg'
import dpgMedia from '~media/logos/dpgmedia.svg'
import expedia from '~media/logos/expedia.svg'
import grubhub from '~media/logos/grubhub.svg'
import heb from '~media/logos/heb.svg'
import jpmc from '~media/logos/jpmc.svg'
import mailchimp from '~media/logos/mailchimp.svg'
import qualtrics from '~media/logos/qualtrics.svg'
import siemens from '~media/logos/siemens.svg'
import target from '~media/logos/target.svg'
import twilio from '~media/logos/twilio.svg'
import underArmour from '~media/logos/underarmour.svg'
import walmart from '~media/logos/walmart.svg'
import workiva from '~media/logos/workiva.svg'

export const charterObj = {
  width: 150,
  src: charter,
  name: 'Charter Communications',
}

export const dpgObj = {
  width: 120,
  src: dpgMedia,
  name: 'DPG Media',
  offset: -9,
}

export const expediaObj = { width: 150, src: expedia, name: 'Expedia' }

export const grubhubObj = { width: 135, src: grubhub, name: 'Grubhub' }

export const hebObj = { width: 115, src: heb, name: 'H‑E‑B' }

export const jpmcObj = { width: 225, src: jpmc, name: 'JPMorgan Chase & Co' }

export const mailchimpObj = { width: 175, src: mailchimp, name: 'MailChimp' }

export const qualtricsObj = {
  width: 135,
  src: qualtrics,
  name: 'Qualtrics',
  offset: 4,
}

export const siemensObj = { width: 140, src: siemens, name: 'Siemens' }

export const targetObj = { width: 60, src: target, name: 'Target' }

export const twilioObj = { width: 125, src: twilio, name: 'Twilio' }

export const underArmourObj = {
  width: 250,
  src: underArmour,
  name: 'Under Armour',
}

export const walmartObj = { width: 160, src: walmart, name: 'Walmart' }

export const workivaObj = {
  width: 150,
  src: workiva,
  name: 'Workiva',
  offset: -5,
}

export const logos = [
  charterObj,
  dpgObj,
  expediaObj,
  grubhubObj,
  hebObj,
  jpmcObj,
  mailchimpObj,
  qualtricsObj,
  siemensObj,
  targetObj,
  twilioObj,
  underArmourObj,
  walmartObj,
  workivaObj,
]

const marquee = keyframes({
  from: {
    transform: 'translateX(100%)',
  },

  to: {
    transform: 'translateX(-100%)',
  },
})

const marquee2 = keyframes({
  from: {
    transform: 'translateX(0%)',
  },

  to: {
    transform: 'translateX(-200%)',
  },
})

// A little trickery do-dad to get the exact width of the current logo-set so our modern infinite marquee 😑 works as expected
// @NOTE Using `theme.space.large` = 32px for offset between each one
const maxWidth =
  logos.reduce((acc, cur) => {
    return acc + cur.width
  }, 0) +
  logos.length * 32 * 2

const CustomerLogos = ({ contrast, ...rest }) => {
  const theme = useTheme()

  const Logos = () => (
    <div
      css={{
        display: 'inline-block',
        willChange: 'transform',

        '&:first-of-type': {
          animation: `${marquee} 90s linear infinite`,
          animationDelay: '-45s',
        },

        '&:nth-of-type(2)': {
          animation: `${marquee2} 90s linear infinite`,
          animationDelay: 0,
        },
      }}
      style={{ width: maxWidth }}
    >
      {logos.map((logo) => (
        <Image
          css={{
            opacity: theme.opacity.base * 0.75,
            marginLeft: theme.space.large,
            marginRight: theme.space.large,

            '.u-contrast &': {
              opacity: theme.opacity.base * 1.5,
              filter: 'brightness(0) invert(1)',
            },
          }}
          key={logo.name}
          style={{
            width: logo.width,
            position: 'relative',
            top: logo.offset ? logo.offset : 0,
          }}
          src={logo.src}
          alt={logo.name}
        />
      ))}
    </div>
  )

  return (
    <Section
      css={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        margin: '0 -50vw',
      }}
      className={clsx({
        'u-contrast': contrast,
      })}
      {...rest}
    >
      <div
        css={{
          maskImage:
            'linear-gradient(90deg, transparent, #000 40%, #000 60%, transparent)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          margin: '0 auto',
        }}
        style={{ maxWidth: `${maxWidth}px` }}
      >
        {/* Duping this so we can use CSS animation to emulate infinite scroll without the need for an external library */}
        <Logos />
        <Logos />
      </div>
    </Section>
  )
}

CustomerLogos.propTypes = {
  contrast: PropTypes.bool,
}

export default CustomerLogos
